// tags API — Phase 2 태그 자동완성·태그별 items·CRUD 엔드포인트 래퍼.
// GET  /api/tags?q=&limit=         자동완성
// GET  /api/tags/:id/items         태그별 items
// POST /api/tags                   태그 단독 생성
// PATCH /api/tags/:id              태그 이름·색상 수정
// DELETE /api/tags/:id             태그 삭제

import { api } from './client';
import type { ItemMeta } from './items';

// ─── 타입 정의 ───────────────────────────────────────────────

/** 태그 기본 정보 */
export interface Tag {
  id: number;
  name: string;
  color: string | null;
}

/** 자동완성 응답 태그 — item_count 포함 */
export interface TagAutocomplete {
  id: number;
  name: string;
  color: string | null;
  /** 이 태그가 붙은 item 수 */
  item_count: number;
}

/** 태그별 items 응답 */
export interface TagItemsResult {
  tag: Tag;
  items: ItemMeta[];
  total: number;
  limit: number;
  offset: number;
}

/** 태그 생성 입력 */
export interface CreateTagInput {
  name: string;
  /** 미지정 시 서버가 이름 해시로 팔레트 색상 자동 부여 */
  color?: string | null;
}

/** 태그 수정 입력 */
export interface UpdateTagInput {
  name?: string;
  color?: string | null;
}

/** 태그별 items 조회 옵션 */
export interface TagItemsOptions {
  limit?: number;
  offset?: number;
}

// ─── API 함수 ────────────────────────────────────────────────

export const tagsApi = {
  /**
   * 태그 자동완성 — prefix LIKE + item_count 내림차순, 동률 시 name ASC.
   * q 미지정 시 전체 태그를 빈도순으로 반환.
   */
  autocompleteTags: (q = '', limit = 20) => {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    qs.set('limit', String(limit));
    return api.get<TagAutocomplete[]>(`/api/tags?${qs.toString()}`);
  },

  /**
   * 특정 태그에 연결된 items 목록 (메타만, payload 제외).
   * pagination 지원.
   */
  getTagItems: (id: number, opts: TagItemsOptions = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const qstr = qs.toString();
    return api.get<TagItemsResult>(`/api/tags/${id}/items${qstr ? `?${qstr}` : ''}`);
  },

  /**
   * 태그 단독 생성.
   * color 미지정 시 서버가 이름 해시로 팔레트 색상 자동 부여.
   * 이름 중복 시 409 (tag_already_exists).
   */
  createTag: (body: CreateTagInput) => api.post<Tag>('/api/tags', body),

  /**
   * 태그 이름·색상 수정.
   * 이름 변경 시 중복 확인 (409 tag_already_exists).
   */
  updateTag: (id: number, patch: UpdateTagInput) => api.patch<Tag>(`/api/tags/${id}`, patch),

  /**
   * 태그 삭제. item_tag_links CASCADE 삭제.
   * 204 응답 (서버).
   */
  deleteTag: (id: number) => api.del<void>(`/api/tags/${id}`),
};
